// src/pages/ingles-online.js
import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useMailer } from '../hooks/useMailer';
import '../style.css';

function InglesOnline() {
  const { send, loading, submitted, ErrorBox } = useMailer('contact');
  const [data, setData] = useState([]);

  useEffect(() => {
    fetch("https://culturlingua.com/api.php")
      .then(r => r.json())
      .then(d => setData(d))
      .catch(e => console.error("Error fetching data:", e));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    send(Object.fromEntries(new FormData(e.target).entries()));
  };

  const advantages = [
    { icon: '💻', title: 'Aula Virtual Interactiva', desc: 'Plataforma con pizarra digital, salas de breakout y herramientas colaborativas en tiempo real.' },
    { icon: '📱', title: 'App Móvil Integrada', desc: 'Accede a tu libro, recursos, tareas y materiales desde cualquier dispositivo.' },
    { icon: '🎮', title: 'Aprendizaje Gamificado', desc: 'Sistema de recompensas, desafíos y juegos educativos que hacen divertido el aprendizaje.' },
    { icon: '📊', title: 'Seguimiento de Progreso', desc: 'Dashboard personalizado con métricas de avance, asistencia y calificaciones en tiempo real.' },
    { icon: '🗣️', title: 'Club de Conversación', desc: 'Sesiones semanales de conversación con estudiantes de diferentes países.' },
    { icon: '📹', title: 'Clases Grabadas', desc: 'Acceso a grabaciones de clase para repasar el contenido a tu ritmo.' },
  ];

  const levels = ['A1 – Principiante', 'A2 – Básico', 'B1 – Pre-Intermedio', 'B2 – Intermedio', 'C1 – Avanzado'];

  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Inglés Online — Culturlingua Virtual | Aprende desde Casa</title>
        <meta name="description" content="Aprende inglés online con profesores certificados. Clases virtuales interactivas con metodología MCER. Todos los niveles A1 a C2. Inscríbete ahora." />
        <meta name="keywords" content="inglés online, clases virtuales, aprender inglés, profesores certificados, MCER, Culturlingua" />
        <meta property="og:title" content="Inglés Online — Culturlingua Virtual" />
        <meta property="og:description" content="Aprende inglés online con profesores certificados desde la comodidad de tu casa." />
      </Helmet>

      {/* ── HERO ── */}
      <section className="hero-modern" style={{minHeight:'80vh'}}>
        <div className="hero-bg"></div>
        <div className="hero-inner" style={{gridTemplateColumns:'1fr', textAlign:'center', justifyItems:'center', paddingBottom:'120px'}}>
          <div>
            <div className="hero-eyebrow">💻 Clases en vivo · A1 a C2 · Desde cualquier lugar</div>
            <h1 className="hero-title" style={{textAlign:'center'}}>
              Aprende Inglés<br /><em>Online</em>
            </h1>
            <p className="hero-sub" style={{margin:'0 auto 40px', textAlign:'center'}}>
              Tu plataforma virtual para dominar el inglés con profesores certificados.
              Clases en vivo, recursos 24/7 y certificación MCER.
            </p>
            <div className="hero-buttons" style={{justifyContent:'center'}}>
              <a href="https://wa.me/5213333449139?text=Hola%2C%20quisiera%20agendar%20una%20clase%20de%20prueba%20online%20gratuita." className="btn-glow" target="_blank" rel="noopener noreferrer">
                🎁 Clase de Prueba Gratuita
              </a>
              <a href="#inscripcion" className="btn-outline">Me interesa →</a>
            </div>
            <div className="trust-badges" style={{justifyContent:'center', marginTop:'32px'}}>
              <span>✅ Clases en vivo</span>
              <span>✅ Acceso 24/7</span>
              <span>✅ Certificación MCER</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAND ── */}
      <div className="stats-band">
        <div className="stats-band-inner">
          {[['A1→C1','Todos los niveles'],['100%','Profesores certificados'],['24/7','Acceso a recursos'],['🏆','Certificación MCER']].map(([n,l]) => (
            <div className="stat-band-item" key={l}>
              <div className="stat-band-num">{n}</div>
              <div className="stat-band-label">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── VENTAJAS ── */}
      <section style={{background:'var(--cream)', padding:'80px 48px'}}>
        <div style={{maxWidth:'1200px', margin:'0 auto'}}>
          <div style={{textAlign:'center', marginBottom:'48px'}}>
            <div className="section-tag">¿Por qué elegirnos?</div>
            <h2 className="section-headline">La ventaja virtual de <em>Culturlingua</em></h2>
            <p className="section-sub" style={{margin:'0 auto'}}>
              Diseñamos una experiencia de aprendizaje inmersiva que combina lo mejor de la enseñanza tradicional con tecnología moderna.
            </p>
          </div>

          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'20px', marginBottom:'64px'}}>
            {advantages.map((a, i) => (
              <div key={i} style={{background:'white', borderRadius:'16px', padding:'28px', boxShadow:'0 2px 12px rgba(0,0,0,0.05)', transition:'transform 0.25s, box-shadow 0.25s'}}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='0 12px 32px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.05)'; }}
              >
                <div style={{fontSize:'2.2rem', marginBottom:'14px'}}>{a.icon}</div>
                <h3 style={{fontFamily:'Fraunces, serif', fontWeight:'700', fontSize:'1.05rem', color:'var(--navy)', marginBottom:'8px'}}>{a.title}</h3>
                <p style={{color:'var(--muted)', fontSize:'0.875rem', lineHeight:'1.6'}}>{a.desc}</p>
              </div>
            ))}
          </div>

          {/* Levels */}
          <div style={{background:'var(--navy)', borderRadius:'20px', padding:'48px', marginBottom:'64px'}}>
            <div style={{textAlign:'center', marginBottom:'32px'}}>
              <div className="section-tag" style={{background:'rgba(245,200,66,0.15)', color:'var(--gold)', borderColor:'rgba(245,200,66,0.3)'}}>Niveles disponibles</div>
              <h2 style={{fontFamily:'Fraunces, serif', fontSize:'1.8rem', color:'white', fontWeight:'900'}}>Desde principiante hasta avanzado</h2>
            </div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:'12px'}}>
              {levels.map((l, i) => (
                <div key={i} style={{background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'10px', padding:'16px', textAlign:'center', color:'white', fontSize:'0.875rem', fontWeight:'500'}}>
                  <span style={{color:'var(--gold)', fontWeight:'700', display:'block', marginBottom:'4px'}}>{l.split(' – ')[0]}</span>
                  {l.split(' – ')[1]}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PRÓXIMOS INICIOS + FORM ── */}
      <section id="inscripcion" style={{background:'white', padding:'80px 48px'}}>
        <div style={{maxWidth:'1200px', margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'80px', alignItems:'start'}}>

          <div>
            <div className="section-tag">Inscripciones abiertas</div>
            <h2 className="section-headline">Próximos<br /><em>Inicios</em></h2>
            <p className="section-sub" style={{marginBottom:'24px'}}>Reserva tu lugar antes de que se llenen los grupos.</p>
            <div className="proximos-dates">
              {data.length > 0 ? data.map((item, index) => (
                <div className={`date-row${index === 0 ? ' next' : ''}`} key={item.id}>
                  <div className={`date-badge${index === 0 ? ' gold-bg' : ''}`}><span>{index === 0 ? '🚀' : '📅'}</span></div>
                  <div className="date-info">
                    <div className="date-label">{item.fecha}</div>
                    <div className="date-meta">Online · Todos los niveles</div>
                  </div>
                  {index === 0 ? <span className="date-next-badge">Próximo</span> : <span className="date-arrow">→</span>}
                </div>
              )) : (
                <div className="date-row" style={{justifyContent:'center', color:'var(--muted)'}}>Cargando fechas...</div>
              )}
            </div>
          </div>

          <div>
            <div style={{background:'var(--cream)', borderRadius:'20px', padding:'40px'}}>
              <h3 style={{fontFamily:'Fraunces, serif', fontSize:'1.4rem', fontWeight:'700', color:'var(--navy)', marginBottom:'8px'}}>
                ¿Te interesa? Déjanos tus datos
              </h3>
              <p style={{color:'var(--muted)', fontSize:'0.875rem', marginBottom:'28px'}}>Te contactamos en menos de 1 hora.</p>
              <form onSubmit={handleSubmit}>
                <div className="form-row" style={{marginBottom:'16px'}}>
                  <div className="form-field" style={{marginBottom:0}}>
                    <label className="form-label">Nombre</label>
                    <input className="form-input" type="text" name="nombre" placeholder="Tu nombre" required />
                  </div>
                  <div className="form-field" style={{marginBottom:0}}>
                    <label className="form-label">WhatsApp</label>
                    <input className="form-input" type="tel" name="telefono" placeholder="+52 33..." required />
                  </div>
                </div>
                <div className="form-field">
                  <label className="form-label">Correo electrónico</label>
                  <input className="form-input" type="email" name="correo" placeholder="tu@correo.com" required />
                </div>
                <div className="form-field">
                  <label className="form-label">¿Alguna pregunta?</label>
                  <textarea className="form-input form-textarea" name="pregunta" placeholder="Horario preferido, nivel aproximado..."></textarea>
                </div>
                {submitted ? (
                  <div style={{textAlign:'center', padding:'16px 0', color:'var(--blue)', fontWeight:'700'}}>✅ ¡Enviado! Te contactamos pronto.</div>
                ) : (
                  <>
                    {ErrorBox}
                    <button type="submit" className="form-submit" disabled={loading}>
                      {loading ? 'Enviando...' : 'Me interesa → Inscribirme'}
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

export default InglesOnline;
